import React, { memo, useCallback, useContext, useMemo } from 'react';

import { getBackendBoolean, nullIfBackendEmptyId } from '@Sage/ssm-utils/backendScalars';
import { Select } from '@Sage/hoi-poi-ui';
import {
    formatProductUnitRelationOptionLabel,
    getProductUnitRelationRowId,
    getSalesOrderLineUomDisplayLabel,
    isSalesOrderLineUomInactive,
    toRelationUomIdString,
} from '@Sage/ssm-utils';

import { PRODUCTS } from 'constants/Entities';
import { getLiteral } from 'utils/getLiteral';
import { getRoute } from 'utils/routes';

import { resolveProductIdFromLine } from './resolveProductIdFromLine';
import { SalesOrderLineUomConfirmContext } from './salesOrderLineUomContext';
import UomSingleValue from './UomSingleValue';
import WarningPopoverIcon from './WarningPopoverIcon';

import './UnitOfMeasureCell.scss';

const UnitOfMeasureCell = memo(function UnitOfMeasureCell(params) {
    const { data, node } = params;
    const isSageImplementation =
        params.isSageImplementation ??
        params.colDef?.cellRendererParams?.isSageImplementation ??
        params.cellRendererParams?.isSageImplementation;
    const {
        pendingRowKey,
        layoutKey,
        onUomChange: onUomChangeFromContext,
    } = useContext(SalesOrderLineUomConfirmContext);

    const onChange =
        onUomChangeFromContext ??
        params.onChange ??
        params.colDef?.cellRendererParams?.onChange ??
        params.cellRendererParams?.onChange;

    const rowIdStable = data?.id ?? data?.Id ?? data?.ID ?? '';
    const rowKeyStable = rowIdStable || `row-index:${node?.rowIndex ?? ''}`;
    const isRowAwaitingUomConfirm =
        pendingRowKey !== null &&
        pendingRowKey !== undefined &&
        pendingRowKey !== '' &&
        String(pendingRowKey) === String(rowKeyStable);
    const selectInstanceKey = isRowAwaitingUomConfirm
        ? `uom-${rowKeyStable}-pending-${layoutKey}`
        : `uom-${rowKeyStable}-idle`;

    const currentId = useMemo(() => {
        const raw = toRelationUomIdString(data?.idRelationUoM);
        return nullIfBackendEmptyId(raw || null) || '';
    }, [data?.idRelationUoM]);

    const selectedLabel = useMemo(() => getSalesOrderLineUomDisplayLabel(data), [data]);

    const options = useMemo(() => {
        const relations = data?.productUnitRelations || [];
        const seen = new Set();
        const opts = [];

        relations.forEach((relation) => {
            const id = toRelationUomIdString(getProductUnitRelationRowId(relation));
            if (!id || seen.has(id)) return;
            seen.add(id);
            const isInactive = !getBackendBoolean(relation?.isActive);
            opts.push({
                value: id,
                label: formatProductUnitRelationOptionLabel(relation),
                isDisabled: isInactive,
                __isInactive: isInactive,
            });
        });

        if (currentId && !seen.has(currentId)) {
            opts.push({
                value: currentId,
                label: selectedLabel || currentId,
                isDisabled: true,
                __isInactive: true,
            });
        }

        return opts;
    }, [data?.productUnitRelations, currentId, selectedLabel]);

    const selectedValue = useMemo(() => {
        if (!currentId) return null;
        return {
            value: currentId,
            label: selectedLabel || currentId,
            __isInactive: isSalesOrderLineUomInactive(data),
        };
    }, [currentId, selectedLabel, data]);

    const handleAddUom = useCallback(() => {
        const productId = resolveProductIdFromLine(data);
        if (productId === null || productId === undefined || productId === '') return;
        const url = getRoute(`${PRODUCTS.route}/${productId}`);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, [data]);

    const handleChange = useCallback(
        (newValue) => {
            if (!onChange) return;
            const rowId = data?.id ?? data?.Id ?? data?.ID ?? '';
            const scalarValue = newValue?.value ?? newValue;
            onChange({
                node,
                rowIndex: node?.rowIndex,
                id: rowId,
                field: 'idRelationUoM',
                value: scalarValue,
            });
        },
        [onChange, node, data],
    );

    const customOption = useCallback((option) => {
        if (option?.__isInactive) {
            return (
                <span className="fm-uom-cell__option">
                    <span className="fm-uom-cell__option-label fm-uom-cell__option-label--inactive">
                        {option.label}
                    </span>
                    <WarningPopoverIcon />
                </span>
            );
        }
        return <span className="fm-uom-cell__option-label">{option.label}</span>;
    }, []);

    const selectAlignRightOverrides = useMemo(
        () => ({
            singleValue: {
                style: {
                    marginLeft: 'auto',
                    justifyContent: 'flex-end',
                    pointerEvents: 'auto',
                    position: 'relative',
                    textAlign: 'right',
                    zIndex: 2,
                },
            },
            placeholder: {
                style: {
                    marginLeft: 'auto',
                },
            },
            optionDisabled: {
                style: {
                    color: 'var(--textLight-disabled)',
                },
            },
            styles: {
                menu: (base) => ({
                    ...base,
                    minWidth: 350,
                }),
            },
        }),
        [],
    );

    const addUomActions = useMemo(() => {
        if (isSageImplementation) return undefined;
        const productId = resolveProductIdFromLine(data);
        if (productId === null || productId === undefined || productId === '') return undefined;
        return [
            {
                label: getLiteral('action_add_unit_of_measure'),
                iconType: 'plus',
                onClick: handleAddUom,
            },
        ];
    }, [data, handleAddUom, isSageImplementation]);

    const uomComponentOverride = useMemo(
        () => ({
            SingleValue: (svProps) => (
                <UomSingleValue {...svProps} lineUomInactive={isSalesOrderLineUomInactive(data)} />
            ),
        }),
        [data],
    );

    return (
        <div className="w5-cell w5-cell--align-right" style={{ width: '100%' }}>
            <Select
                key={selectInstanceKey}
                value={selectedValue}
                options={options}
                onChange={handleChange}
                placeholder={getLiteral('label_selectone')}
                isClearable={false}
                isFullWidth
                shouldSetValueOnChange={false}
                actions={addUomActions}
                customOption={customOption}
                componentOverride={uomComponentOverride}
                overrides={selectAlignRightOverrides}
            />
        </div>
    );
});

export default UnitOfMeasureCell;
